// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import type {
  AttentionThresholdSetting,
  UpdateAttentionThresholdsInput,
} from '@/account/api/types';
import { getAttentionThresholdSettingKey } from '@/account/constants/attention-threshold-labels';
import { z } from 'zod';

export const THRESHOLD_TIER_FIELDS = [
  'noticeDays',
  'warningDays',
  'criticalDays',
] as const;

export type ThresholdTierField = (typeof THRESHOLD_TIER_FIELDS)[number];

const THRESHOLD_TIER_LABELS: Record<ThresholdTierField, string> = {
  noticeDays: 'Notice',
  warningDays: 'Warning',
  criticalDays: 'Critical',
};

const thresholdDaysSchema = z
  .number({ invalid_type_error: 'Whole days only' })
  .int('Whole days only')
  .nonnegative('Must be zero or greater')
  .nullable();

function isBlankThresholdValue(value: number | null): boolean {
  return value === null;
}

type CompleteThresholdFormRowData = Record<ThresholdTierField, number>;

function isCompleteThresholdRow(
  row: Record<ThresholdTierField, number | null>
): row is CompleteThresholdFormRowData {
  return THRESHOLD_TIER_FIELDS.every(
    (field) => !isBlankThresholdValue(row[field])
  );
}

const thresholdFormRowSchema = z
  .object({
    noticeDays: thresholdDaysSchema,
    warningDays: thresholdDaysSchema,
    criticalDays: thresholdDaysSchema,
  })
  .superRefine((row, ctx) => {
    const values = THRESHOLD_TIER_FIELDS.map((field) => row[field]);
    const hasAnyValue = values.some((value) => !isBlankThresholdValue(value));
    if (!hasAnyValue) {
      return;
    }

    const missingFields = THRESHOLD_TIER_FIELDS.filter((field) =>
      isBlankThresholdValue(row[field])
    );
    missingFields.forEach((field) => {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [field],
        message: `${THRESHOLD_TIER_LABELS[field]} is required`,
      });
    });

    if (missingFields.length > 0) {
      return;
    }

    if (!isCompleteThresholdRow(row)) {
      return;
    }

    if (row.noticeDays > row.warningDays) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['warningDays'],
        message: 'Must be the same or greater than notice days',
      });
    }
    if (row.warningDays > row.criticalDays) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['criticalDays'],
        message: 'Must be the same or greater than warning days',
      });
    }
  });

export const attentionThresholdRowsSchema = z.record(thresholdFormRowSchema);

export type ThresholdFormRowData = z.infer<typeof thresholdFormRowSchema>;
export type AttentionThresholdRowsByKey = z.infer<
  typeof attentionThresholdRowsSchema
>;
export interface AttentionThresholdFormFields {
  thresholds: AttentionThresholdRowsByKey;
}

function toThresholdRowData(
  setting: AttentionThresholdSetting
): ThresholdFormRowData {
  return {
    noticeDays: setting.override === null ? null : setting.override.noticeDays,
    warningDays:
      setting.override === null ? null : setting.override.warningDays,
    criticalDays:
      setting.override === null ? null : setting.override.criticalDays,
  };
}

export function toAttentionThresholdRowsByKey(
  settings: AttentionThresholdSetting[]
): AttentionThresholdRowsByKey {
  return Object.fromEntries(
    settings.map((setting) => [
      getAttentionThresholdSettingKey(setting),
      toThresholdRowData(setting),
    ])
  );
}

export function getAttentionThresholdRow(
  rows: AttentionThresholdRowsByKey | undefined,
  settingKey: string
): ThresholdFormRowData | undefined {
  return rows === undefined
    ? undefined
    : (rows as Partial<Record<string, ThresholdFormRowData>>)[settingKey];
}

function rowHasOverride(
  row: ThresholdFormRowData
): row is CompleteThresholdFormRowData {
  return isCompleteThresholdRow(row);
}

export function buildAttentionThresholdsInput(
  rows: AttentionThresholdRowsByKey,
  settings: AttentionThresholdSetting[]
): UpdateAttentionThresholdsInput {
  const thresholds: UpdateAttentionThresholdsInput['thresholds'] = [];

  settings.forEach((setting) => {
    const row = getAttentionThresholdRow(
      rows,
      getAttentionThresholdSettingKey(setting)
    );
    if (row === undefined || !rowHasOverride(row)) {
      return;
    }

    thresholds.push({
      entity: setting.entity,
      reason: setting.reason,
      noticeDays: row.noticeDays,
      warningDays: row.warningDays,
      criticalDays: row.criticalDays,
    });
  });

  return { thresholds };
}
